import { useEffect, useState } from "react";

/** The four poses, all cut to the same canvas so they line up: two walking steps, pushing the stick into the ground, and waving. */
const IMG = {
  walkA: "/illustrations/mascot-walk-1.webp",
  walkB: "/illustrations/mascot-walk-2.webp",
  plant: "/illustrations/mascot-plant.webp",
  wave: "/illustrations/mascot-wave.webp",
};
/** Image size ratio (width / height) of the poses. */
const RATIO = "617 / 481";
/** Where the foot of the signpost is, as a percentage of the image (width, height). The dust puffs start here. */
const STICK_X = 32.7;
const STICK_Y = 84.2;

/**
 * Timeline in ms, unhurried on purpose so it reads as someone walking up and doing a job:
 * walks in (slowing as it nears the spot) -> stops for a beat -> pushes the stick in three times -> pauses -> waves.
 */
const WALK_MS = 2400;
const PLANT_MS = 2900;
const PUSH_GAP = 750;
const PUSH_2_MS = PLANT_MS + PUSH_GAP;
const PUSH_3_MS = PLANT_MS + PUSH_GAP * 2;
const WAVE_MS = PUSH_3_MS + 900;
/** How long each walking pose stays up; the gaps grow as it slows down to stop. */
const STEP_GAPS = [300, 300, 310, 320, 340, 360, 390, 430, 480];

const DUST = [
  { dx: "-46px", dy: "-6px", s: 11 }, { dx: "-28px", dy: "-18px", s: 8 }, { dx: "-10px", dy: "-26px", s: 7 },
  { dx: "18px", dy: "-22px", s: 8 }, { dx: "36px", dy: "-12px", s: 10 }, { dx: "54px", dy: "-3px", s: 12 },
];
const SKID = [{ s: 12, d: 0 }, { s: 9, d: 0.07 }, { s: 14, d: 0.14 }, { s: 8, d: 0.21 }];

const CSS = `
@keyframes nf-walkin { 0% { opacity: 0; transform: translateX(105%); } 8% { opacity: 1; } 100% { opacity: 1; transform: none; } }
@keyframes nf-hop { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-2.2%) rotate(-.6deg); } }
@keyframes nf-thump { 0%,100% { transform: none; } 30% { transform: translateY(1.8%) scale(1.012,.982); } 60% { transform: translateY(-.4%); } }
@keyframes nf-breathe { 0%,100% { transform: scaleY(1); } 50% { transform: scaleY(1.012); } }
@keyframes nf-shadow { 0% { opacity: 0; } 100% { opacity: 1; } }
@keyframes nf-dust { 0% { opacity: .8; transform: translate(0,0) scale(.3); } 100% { opacity: 0; transform: translate(var(--dx),var(--dy)) scale(1.5); } }
@keyframes nf-skid { 0% { opacity: .7; transform: translate(0,0) scale(.4); } 100% { opacity: 0; transform: translate(40px,-6px) scale(1.5); } }
.nf-walkin { animation: nf-walkin ${WALK_MS}ms cubic-bezier(.3,.3,.45,1) both; }
.nf-hop { animation: nf-hop .36s ease-in-out 7 both; }
.nf-live { transform-origin: 50% 96%; animation: nf-thump .4s ease-out ${PLANT_MS}ms both, nf-thump .4s ease-out ${PUSH_2_MS}ms both, nf-thump .4s ease-out ${PUSH_3_MS}ms both, nf-breathe 3.8s ease-in-out ${WAVE_MS}ms infinite; }
.nf-shadow { animation: nf-shadow .6s ease-out .4s both; }
.nf-dust1 { animation: nf-dust .9s ease-out ${PLANT_MS}ms both; }
.nf-dust2 { animation: nf-dust .8s ease-out ${PUSH_2_MS}ms both; }
.nf-dust3 { animation: nf-dust .7s ease-out ${PUSH_3_MS}ms both; }
.nf-skid { animation: nf-skid .8s ease-out calc(${WALK_MS - 500}ms + var(--d)) both; }
@media (prefers-reduced-motion: reduce) { .nf-walkin, .nf-hop, .nf-live, .nf-shadow, .nf-dust1, .nf-dust2, .nf-dust3, .nf-skid { animation: none; } .nf-dust1, .nf-dust2, .nf-dust3, .nf-skid { display: none; } }
`;

type Phase = "walk" | "plant" | "wave";

/**
 * The 404 mascot: walks in with a smile, pushes the signpost into the ground (three thumps, each with a puff of dust), then waves.
 * Click it to replay. With "reduce motion" on, it just shows the final waving pose.
 */
export function NotFoundMascot() {
  const [run, setRun] = useState(0);
  const [phase, setPhase] = useState<Phase>("walk");
  const [flip, setFlip] = useState(false);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setPhase("wave");
      return;
    }
    setPhase("walk");
    setFlip(false);
    const timers: number[] = [];
    let at = 0;
    for (const gap of STEP_GAPS) {
      at += gap;
      if (at >= WALK_MS) break;
      timers.push(window.setTimeout(() => setFlip((f) => !f), at));
    }
    timers.push(window.setTimeout(() => setPhase("plant"), PLANT_MS));
    timers.push(window.setTimeout(() => setPhase("wave"), WAVE_MS));
    return () => timers.forEach(window.clearTimeout);
  }, [run]);

  const shown = phase === "walk" ? (flip ? IMG.walkB : IMG.walkA) : phase === "plant" ? IMG.plant : IMG.wave;

  return (
    <div className="relative mx-auto w-64 sm:w-80" style={{ aspectRatio: RATIO }}>
      <style>{CSS}</style>
      <div className="absolute inset-0 mx-auto h-4/5 w-4/5 rounded-full bg-gradient-to-b from-orange-100 to-transparent blur-2xl" aria-hidden />
      <div key={run} className="absolute inset-0">
        <div className="nf-walkin h-full w-full">
          <div className="nf-shadow absolute bottom-[1.5%] left-[14%] h-[5%] w-[58%] rounded-[50%] bg-black/15 blur-[3px]" aria-hidden />
          <div className="nf-live h-full w-full">
            <div className="nf-hop h-full w-full">
              <button type="button" onClick={() => setRun((r) => r + 1)} className="relative block h-full w-full cursor-pointer select-none border-0 bg-transparent p-0 focus-visible:outline-none" aria-label="Replay animation" title="Click to replay">
                {Object.values(IMG).map((src) => (
                  <img key={src} src={src} alt="" draggable={false} className="absolute inset-0 h-full w-full object-contain drop-shadow-xl" style={{ opacity: src === shown ? 1 : 0 }} />
                ))}
              </button>
            </div>
          </div>
        </div>

        {/* skid dust as it comes to a stop */}
        <div className="pointer-events-none absolute" style={{ left: "58%", top: "96%" }} aria-hidden>
          {SKID.map((d, i) => (
            <span key={i} className="nf-skid absolute rounded-full bg-[#e5d9c3]" style={{ width: d.s, height: d.s, left: i * 5, top: -d.s / 2, ["--d" as string]: `${d.d}s` }} />
          ))}
        </div>
        {/* dust at the foot of the stick: a big puff on the first push, smaller ones on the next two */}
        <div className="pointer-events-none absolute" style={{ left: `${STICK_X}%`, top: `${STICK_Y}%` }} aria-hidden>
          {DUST.map((d, i) => (
            <span key={`a${i}`} className="nf-dust1 absolute rounded-full bg-[#d8c7a8]" style={{ width: d.s, height: d.s, left: -d.s / 2, top: -d.s / 2, ["--dx" as string]: d.dx, ["--dy" as string]: d.dy }} />
          ))}
          {DUST.filter((_, i) => i % 2 === 0).map((d, i) => (
            <span key={`b${i}`} className="nf-dust2 absolute rounded-full bg-[#d8c7a8]" style={{ width: d.s * 0.8, height: d.s * 0.8, left: -d.s * 0.4, top: -d.s * 0.4, ["--dx" as string]: d.dx, ["--dy" as string]: d.dy }} />
          ))}
          {DUST.filter((_, i) => i % 3 === 0).map((d, i) => (
            <span key={`c${i}`} className="nf-dust3 absolute rounded-full bg-[#d8c7a8]" style={{ width: d.s * 0.6, height: d.s * 0.6, left: -d.s * 0.3, top: -d.s * 0.3, ["--dx" as string]: d.dx, ["--dy" as string]: d.dy }} />
          ))}
        </div>
      </div>
    </div>
  );
}